/** @module The collection's value: the big figure, its change and a bar chart over a range. */
import { useQuery } from "@tanstack/react-query";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, type components, problemFrom } from "@/api/client";
import { BarChart, sample } from "@/components/ui/bar-chart";
import { Segmented } from "@/components/ui/controls";
import { count, dayMonth, money } from "@/lib/format";

type Summary = components["schemas"]["DashboardSummaryOut"];
type Point = components["schemas"]["ValuePointOut"];
type Range = "30d" | "90d" | "1y" | "all";

const RANGES: Range[] = ["30d", "90d", "1y", "all"];
async function fetchHistory(range: Range): Promise<Point[]> {
  const { data, response } = await api.GET("/api/v1/dashboard/value-history", {
    params: { query: { range } },
  });
  if (!data) throw problemFrom(response);
  return data;
}

export function ValueCard({ summary, compact = false }: { summary: Summary; compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const [range, setRange] = useState<Range>("30d");
  const history = useQuery({
    queryKey: ["dashboard", "value-history", range],
    queryFn: () => fetchHistory(range),
  });

  const points = sample(history.data ?? []);
  const first = points[0];
  const last = points[points.length - 1];
  const change = first && last ? Number(last.total_value) - Number(first.total_value) : null;
  const percent =
    change !== null && first && Number(first.total_value) > 0
      ? (change / Number(first.total_value)) * 100
      : null;
  const up = (change ?? 0) >= 0;
  const middle = points[Math.floor(points.length / 2)];

  return (
    <div className="flex flex-col gap-4">
      <div className="row-between flex-wrap items-start">
        <div className="flex flex-col gap-1">
          <span className="t-label">{t("dashboard.totalValue")}</span>
          <div className="row flex-wrap items-baseline gap-3">
            <span className={`num-xl ${compact ? "" : "md:text-[40px]"}`}>
              {money(summary.total_value, locale) ?? "–"}
            </span>
            {change !== null && percent !== null && points.length > 1 ? (
              <span className={`badge ${up ? "badge-gain" : "badge-loss"}`}>
                {up ? <ArrowUpRight /> : <ArrowDownRight />}
                {up ? "+" : "−"}
                {money(Math.abs(change), locale)} · {up ? "+" : "−"}
                {Math.abs(percent).toLocaleString(locale, { maximumFractionDigits: 1 })} %
              </span>
            ) : null}
          </div>
          <span className="t-sm t-muted">
            {t("dashboard.cardsLine", {
              count: summary.item_count,
              cards: count(summary.item_count, locale),
            })}
            {summary.unvalued_count > 0 ? (
              <>
                {" · "}
                <span>{t("dashboard.unvaluedLine", { count: summary.unvalued_count })}</span>
              </>
            ) : null}
            {summary.suspicious_count > 0 ? (
              <>
                {" · "}
                <span>{t("dashboard.suspiciousLine", { count: summary.suspicious_count })}</span>
              </>
            ) : null}
            {summary.unvalued_count + summary.suspicious_count > 0
              ? ` – ${t("dashboard.notInTotal")}`
              : ""}
          </span>
        </div>
        {/* The phone shows the last 30 days only, as in the design. */}
        <Segmented
          className={compact ? "hidden" : ""}
          label={t("dashboard.history")}
          value={range}
          onChange={setRange}
          options={RANGES.map((option) => ({
            value: option,
            label: t(`dashboard.rangeShort.${option}`),
          }))}
        />
      </div>
      {points.length < 2 ? (
        <p className="t-sm t-muted">
          {history.isPending ? t("common.loading") : t("dashboard.historyEmpty")}
        </p>
      ) : (
        <BarChart
          className={compact ? "h-16" : "h-16 md:h-[180px]"}
          label={t("dashboard.historyLabel", {
            from: money(first?.total_value, locale),
            to: money(last?.total_value, locale),
          })}
          bars={points.map((point) => ({
            key: point.captured_on,
            value: Number(point.total_value),
            title: `${dayMonth(point.captured_on, locale)}: ${money(point.total_value, locale) ?? "–"}`,
          }))}
          axis={[
            first ? dayMonth(first.captured_on, locale) : "",
            middle ? dayMonth(middle.captured_on, locale) : "",
            t("dashboard.today"),
          ]}
        />
      )}
    </div>
  );
}
